// Safe localStorage wrapper.
//
// Accessing localStorage *throws* in some contexts rather than returning null:
// Safari private browsing, browsers configured to block site data, and some
// embedded webviews. Every call is therefore wrapped, and every read has a
// fallback so the app renders correctly when storage is unavailable.
//
// All keys are namespaced `jx.` to avoid collisions with anything else served
// from the same origin.

const PREFIX = 'jx.';

const namespaced = (key) => `${PREFIX}${key}`;

// Probe once: if localStorage is entirely unavailable, skip the try/catch churn.
const isAvailable = (() => {
  try {
    const probe = `${PREFIX}__probe__`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
})();

export const get = (key, fallback = null) => {
  if (!isAvailable) return fallback;
  try {
    const raw = window.localStorage.getItem(namespaced(key));
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch {
    // Unparseable value (hand-edited, or written by an older version).
    return fallback;
  }
};

export const set = (key, value) => {
  if (!isAvailable) return false;
  try {
    window.localStorage.setItem(namespaced(key), JSON.stringify(value));
    return true;
  } catch {
    // Most likely QuotaExceededError.
    return false;
  }
};

export const remove = (key) => {
  if (!isAvailable) return false;
  try {
    window.localStorage.removeItem(namespaced(key));
    return true;
  } catch {
    return false;
  }
};

// Key names used across the app, collected here so they're greppable.
export const KEYS = {
  SEEN_INSTRUCTIONS: 'seenInstructions',
  STATS_STRIP_DISMISSED: 'statsStripDismissed',
  A2HS_DISMISSED_AT: 'a2hsDismissedAt',
};

const storage = { get, set, remove, KEYS, isAvailable };

export default storage;
